import { SyntheticEvent, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DialogProps } from '@mui/material';

import { useMutationRequestBattery } from '~hooks/useMutationRequestBattery';
import { useMainContext } from '~providers';
import { BatteryRequest } from '~types';

export const useRentCharger = () => {
  const { rentalQuery, saveRentalResult } = useMainContext();
  const [searchParams] = useSearchParams();
  const [isRentingCharger, setIsRentingCharger] = useState(false);
  const [isPaymentErrorDialogOpen, setPaymentErrorDialogOpen] = useState(false);
  // Card declines open the payment-error dialog; every other failure — a pass
  // the user already holds, a pricing not offered here — must still be toasted,
  // or the Rent Charger button just stops spinning with no explanation.
  const requestBatteryMutation = useMutationRequestBattery({
    onPaymentError: () => {
      setPaymentErrorDialogOpen(true);
    },
  });

  const handleClosePaymentErrorDialog: DialogProps['onClose'] = (
    event,
    reason,
  ) => {
    if (reason && reason === 'backdropClick') return;
    setPaymentErrorDialogOpen(false);
  };

  const handleSetIsRentingCharger = useCallback((isRenting: boolean) => {
    setIsRentingCharger(isRenting);
  }, []);

  const handleRentCharger = async (
    paymentMethod?: BatteryRequest['paymentMethod'],
    event?: SyntheticEvent<HTMLButtonElement>,
    userSubscriptionId?: string,
    pricingId?: string,
  ) => {
    try {
      event?.preventDefault();
      setIsRentingCharger(true);

      const stationSerialNumber = searchParams.get('box');
      if (stationSerialNumber) {
        await requestBatteryMutation.mutateAsync(
          {
            paymentMethod,
            stationSerialNumber: stationSerialNumber,
            userSubscriptionId: userSubscriptionId,
            pricingId: pricingId,
          },
          {
            onSuccess: (res) => {
              saveRentalResult(res.data ?? {});

              // Delay 5 seconds to refetch rental state
              setTimeout(() => {
                rentalQuery?.refetch();
                setIsRentingCharger(false);
              }, 5000);
            },
          },
        );
      }
    } catch (err) {
      setIsRentingCharger(false);
    }
  };

  return {
    handleRentCharger,
    handleSetIsRentingCharger,
    isRentingCharger: !!(rentalQuery?.isFetching || isRentingCharger),
    isPaymentErrorDialogOpen,
    handleClosePaymentErrorDialog,
  };
};
